import { useEffect, useMemo, useRef, useState } from 'react';
import { api, type Field, type Meta, type Source, type Step, type StepDef } from '../api';
import { Button, Field as FormField, Input, Select, ErrorNote } from './ui';

/**
 * Editing one field, with the value shown after every transform step.
 *
 * The per-step preview is the point: a pipeline that produces the wrong number is easy to
 * build and hard to debug when you can only see the end of it. Each step's output is
 * obtained by sending the API one candidate field per prefix of the step list, so all of
 * them are evaluated against a SINGLE upstream fetch rather than one request per step.
 */

export interface DraftField {
  id?: string;
  key: string;
  label: string;
  unit: string;
  extractor: Field['extractor'];
  expr: string;
  attr: string;
  time_expr: string;
  transforms: Step[];
  datatype: Field['datatype'];
  fallback: string | null;
}

export const emptyDraft = (): DraftField => ({
  key: '',
  label: '',
  unit: '',
  extractor: 'json',
  expr: '',
  attr: 'text',
  time_expr: '',
  transforms: [],
  datatype: 'number',
  fallback: null,
});

export const draftFrom = (f: Field): DraftField => ({
  id: f.id,
  key: f.key,
  label: f.label,
  unit: f.unit,
  extractor: f.extractor,
  expr: f.expr,
  attr: f.attr,
  time_expr: f.time_expr,
  transforms: f.transforms ?? [],
  datatype: f.datatype,
  fallback: f.fallback,
});

interface StepResult {
  value?: string;
  error?: string;
}

/**
 * What to show for the raw extraction, before any step has run.
 *
 * A series extractor legitimately cannot produce a single value at this point — that is
 * what the steps are for — so the coercion error it returns is expected rather than a
 * fault. Showing it in red taught people to ignore red, which is worse than showing
 * nothing.
 */
function extractedNote(draft: DraftField, result: StepResult | undefined) {
  if (draft.extractor === 'series' && result?.error?.includes('time series')) {
    return (
      <span className="mono text-xs text-ink-500 truncate max-w-[60%]">
        time series — add a step such as pick_now
      </span>
    );
  }
  if (result?.error) {
    return <span className="mono text-xs text-bad truncate max-w-[60%]">{result.error}</span>;
  }
  return <span className="mono text-xs text-ink-300 truncate max-w-[60%]">{result?.value ?? '…'}</span>;
}

function StepRow({
  step,
  def,
  result,
  onChange,
  onRemove,
  onMove,
}: {
  step: Step;
  def: StepDef | undefined;
  result: StepResult | undefined;
  onChange: (next: Step) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
}) {
  return (
    <div className="border border-ink-800 rounded-md bg-ink-950">
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-ink-800">
        <span className="mono text-xs text-accent">{step.op}</span>
        <span className="text-xs text-ink-500 truncate flex-1">{def?.summary ?? 'unknown step'}</span>
        {result?.error ? (
          <span className="mono text-xs text-bad truncate max-w-[40%]" title={result.error}>
            {result.error}
          </span>
        ) : (
          <span className="mono text-xs text-good truncate max-w-[40%]">{result?.value ?? '…'}</span>
        )}
        <div className="flex items-center gap-0.5 shrink-0">
          <button onClick={() => onMove(-1)} className="px-1 text-ink-500 hover:text-ink-100" title="Move up">
            ↑
          </button>
          <button onClick={() => onMove(1)} className="px-1 text-ink-500 hover:text-ink-100" title="Move down">
            ↓
          </button>
          <button onClick={onRemove} className="px-1 text-ink-500 hover:text-bad" title="Remove">
            ×
          </button>
        </div>
      </div>

      {def && def.args.length > 0 && (
        <div className="grid grid-cols-2 gap-2 p-2.5">
          {def.args.map((arg) => (
            <label key={arg.name} className="block">
              <span className="block text-[11px] text-ink-500 mb-0.5" title={arg.summary}>
                {arg.name}
                {arg.required && <span className="text-bad"> *</span>}
              </span>
              <Input
                mono
                value={String(step[arg.name] ?? '')}
                placeholder={arg.summary}
                onChange={(e) => onChange({ ...step, [arg.name]: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function FieldEditor({
  source,
  meta,
  draft,
  onDraftChange,
  onSaved,
  onCancel,
  canEdit,
}: {
  source: Source;
  meta: Meta;
  draft: DraftField;
  onDraftChange: (next: DraftField) => void;
  onSaved: () => void;
  onCancel: () => void;
  canEdit: boolean;
}) {
  const [stepResults, setStepResults] = useState<Record<number, StepResult>>({});
  const [finalResult, setFinalResult] = useState<StepResult | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const stepDefs = useMemo(() => new Map(meta.steps.map((s) => [s.op, s])), [meta.steps]);
  const grouped = useMemo(() => {
    const groups = new Map<string, StepDef[]>();
    for (const s of meta.steps) {
      const list = groups.get(s.group) ?? [];
      list.push(s);
      groups.set(s.group, list);
    }
    return [...groups.entries()];
  }, [meta.steps]);

  /**
   * Re-evaluate on every edit, debounced. One request carries the extraction alone plus
   * each successive prefix of the steps, so the whole pipeline is traced from a single fetch.
   */
  useEffect(() => {
    // Nothing to trace against until a push source has received something.
    if (source.mode === 'push' && !source.last_payload) {
      setStepResults({});
      setFinalResult({ error: 'waiting for the first payload' });
      return;
    }
    if (!draft.expr && draft.extractor !== 'const') {
      setStepResults({});
      setFinalResult(null);
      return;
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const base = {
        extractor: draft.extractor,
        expr: draft.expr,
        attr: draft.attr,
        time_expr: draft.time_expr,
        datatype: draft.datatype,
        fallback: draft.fallback,
      };
      const probes = [
        { ...base, key: 'step_0', transforms: [] },
        ...draft.transforms.map((_, i) => ({
          ...base,
          key: `step_${i + 1}`,
          transforms: draft.transforms.slice(0, i + 1),
        })),
      ];

      // A push source has no URL, so the trace runs against whatever it last received.
      const target =
        source.mode === 'push'
          ? { payload: source.last_payload ?? '' }
          : { url: source.url ?? '', method: source.method, headers: source.headers };

      void api
        .preview({ ...target, kind: source.kind, timezone: source.timezone, fields: probes })
        .then((res) => {
          const next: Record<number, StepResult> = {};
          for (const f of res.fields ?? []) {
            const index = Number(f.key.replace('step_', ''));
            next[index] = f.ok ? { value: f.value } : { error: f.error };
          }
          setStepResults(next);
          setFinalResult(next[draft.transforms.length] ?? null);
        })
        .catch((e: Error) => setFinalResult({ error: e.message }));
    }, 450);
    return () => window.clearTimeout(timer.current);
  }, [draft, source]);

  const set = <K extends keyof DraftField>(key: K, value: DraftField[K]): void =>
    onDraftChange({ ...draft, [key]: value });

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const body = { ...draft };
      if (draft.id) await api.updateField(draft.id, body);
      else await api.createField(source.id, body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const needsTimeExpr = draft.extractor === 'series';
  const needsAttr = draft.extractor === 'css' || draft.extractor === 'xml';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Key" hint="Used in the Loxone payload as <key>value">
          <Input mono value={draft.key} onChange={(e) => set('key', e.target.value)} placeholder="price_now" />
        </FormField>
        <FormField label="Label">
          <Input value={draft.label} onChange={(e) => set('label', e.target.value)} placeholder="Price now" />
        </FormField>
        <FormField label="Type">
          <Select value={draft.datatype} onChange={(e) => set('datatype', e.target.value as Field['datatype'])}>
            {meta.datatypes.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Unit">
          <Input value={draft.unit} onChange={(e) => set('unit', e.target.value)} placeholder="c/kWh" />
        </FormField>
      </div>

      {draft.datatype === 'text' && (
        <div className="text-xs text-warn bg-warn/10 border border-warn/30 rounded-md px-3 py-2">
          A Virtual HTTP Input captures numbers only. Text fields are served and stored, but a
          Miniserver cannot poll them without one Virtual Input per character.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        <FormField label="Extractor">
          <Select value={draft.extractor} onChange={(e) => set('extractor', e.target.value as Field['extractor'])}>
            {meta.extractors.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
        </FormField>
        <div className={needsTimeExpr || needsAttr ? '' : 'col-span-2'}>
          <FormField label={draft.extractor === 'series' ? 'Values path' : 'Expression'}>
            <Input mono value={draft.expr} onChange={(e) => set('expr', e.target.value)} placeholder="current.temperature_2m" />
          </FormField>
        </div>
        {needsTimeExpr && (
          <FormField label="Timestamps path">
            <Input mono value={draft.time_expr} onChange={(e) => set('time_expr', e.target.value)} placeholder="unix_seconds" />
          </FormField>
        )}
        {needsAttr && (
          <FormField label="Attribute" hint="text, html, or an attribute name">
            <Input mono value={draft.attr} onChange={(e) => set('attr', e.target.value)} placeholder="text" />
          </FormField>
        )}
      </div>

      {needsTimeExpr && !source.timezone && (
        <div className="text-xs text-warn bg-warn/10 border border-warn/30 rounded-md px-3 py-2">
          This source has no timezone set. Timestamps without an offset (such as
          <span className="mono"> 2026-07-31T14:00</span>) cannot be read correctly without one.
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs uppercase tracking-wide text-ink-500">
            Transform steps — value shown after each
          </span>
          {canEdit && (
            <div className="relative">
              <Button onClick={() => setAddOpen(!addOpen)}>Add step</Button>
              {addOpen && (
                <div className="absolute right-0 z-20 mt-1 w-72 max-h-80 overflow-auto bg-ink-900 border border-ink-800 rounded-md shadow-xl">
                  {grouped.map(([group, defs]) => (
                    <div key={group}>
                      <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-ink-500 bg-ink-850 sticky top-0">
                        {group}
                      </div>
                      {defs.map((d) => (
                        <button
                          key={d.op}
                          onClick={() => {
                            set('transforms', [...draft.transforms, { op: d.op }]);
                            setAddOpen(false);
                          }}
                          className="block w-full text-left px-3 py-1.5 hover:bg-ink-800"
                        >
                          <span className="mono text-xs text-accent">{d.op}</span>
                          <span className="block text-[11px] text-ink-500">{d.summary}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 px-2.5 py-1.5 border border-ink-800 rounded-md bg-ink-950">
            <span className="mono text-xs text-ink-500">extracted</span>
            <span className="flex-1" />
            {extractedNote(draft, stepResults[0])}
          </div>

          {draft.transforms.map((step, i) => (
            <StepRow
              key={`${step.op}-${i}`}
              step={step}
              def={stepDefs.get(step.op)}
              result={stepResults[i + 1]}
              onChange={(next) => set('transforms', draft.transforms.map((s, j) => (j === i ? next : s)))}
              onRemove={() => set('transforms', draft.transforms.filter((_, j) => j !== i))}
              onMove={(delta) => {
                const target = i + delta;
                if (target < 0 || target >= draft.transforms.length) return;
                const next = [...draft.transforms];
                const [moved] = next.splice(i, 1);
                next.splice(target, 0, moved!);
                set('transforms', next);
              }}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <span className="text-xs uppercase tracking-wide text-ink-500">Result</span>
        {finalResult?.error ? (
          <span className="mono text-sm text-bad">{finalResult.error}</span>
        ) : (
          <span className="mono text-sm text-good">{finalResult?.value ?? '…'}</span>
        )}
        {draft.unit && !finalResult?.error && <span className="text-xs text-ink-500">{draft.unit}</span>}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {canEdit && (
        <div className="flex gap-2">
          <Button variant="primary" onClick={() => void save()} disabled={saving || !draft.key}>
            {saving ? 'Saving…' : draft.id ? 'Save field' : 'Add field'}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
